import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { num } from '../lib/format.js';
import { hrefFor } from '../lib/router.js';
import { EmptyState, ErrorState, Loading, Panel, StatusBadge, Tile } from '../components/ui.js';

interface Sample {
  label: string;
  contactId: string | null;
  detail: string | null;
}
interface Category {
  key: string;
  count: number;
  samples: Sample[];
}
interface Report {
  readOnly: true;
  totals: Record<string, number>;
  issues: Category[];
}

const TOTAL_LABEL: Array<[string, string]> = [
  ['contacts', 'Kontaktů celkem'],
  ['withEmail', 'Se skutečným e-mailem'],
  ['withAliases', 'S dalšími (starými) adresami'],
  ['withOrders', 'S objednávkou'],
  ['withoutOrders', 'Bez objednávky'],
  ['consentMarketing', 'Souhlas: marketing'],
  ['consentWatchdog', 'Souhlas: hlídací psi'],
  ['withTrip', 'S cestou'],
  ['withMessages', 'S historií zpráv'],
];

const ISSUE: Record<string, { title: string; help: string; sample: string }> = {
  without_consent: {
    title: 'Bez uděleného souhlasu',
    help: 'Hub jim neposílá marketing ani hlídače. Je to v pořádku, pokud je import nebo e-shop souhlas nedal.',
    sample: 'E-mail',
  },
  duplicate_emails: {
    title: 'Stejný e-mail u více kontaktů',
    help: 'Ukazuje první z nich. Takové kontakty se nespojily; zkontrolujte, jestli nemají společný zákaznický účet.',
    sample: 'E-mail',
  },
  orphan_orders: {
    title: 'Objednávky bez použitelného kontaktu',
    help: 'Kontakt je smazaný nebo sloučený (nebo chybí).',
    sample: 'Objednávka',
  },
  orphan_events: {
    title: 'Přijaté události bez kontaktu',
    help: 'Objednávky a souhlasy, u nichž se nepodařilo najít kontakt. Po výmazu kontaktu je to očekávané.',
    sample: 'Objednávka / zákazník',
  },
  orphan_gifts: {
    title: 'Dárky bez kupujícího',
    help: 'Dárek nemá platný kontakt kupujícího.',
    sample: 'Dárek',
  },
  placeholder_email: {
    title: 'Kontakty se zástupnou adresou',
    help: 'E-shop poslal objednávku bez e-mailu, hub vyrobil adresu končící na unknown.invalid. Zprávám se nikam nedoručí.',
    sample: 'E-mail',
  },
  orders_unknown_race: {
    title: 'Objednávky na neexistující závod',
    help: 'Závod z objednávky nebo její položky hub nezná. Obvykle chybí událost o závodu.',
    sample: 'Objednávka',
  },
};

/**
 * Kontrolní report po importu: spojují se data kontaktů? Jen čtení; spouští se tlačítkem,
 * protože na velkém importu chvíli trvá.
 */
export function ContactLinkage(): JSX.Element {
  const [started, setStarted] = useState(false);
  const query = useQuery<Report>({
    queryKey: ['contact-linkage'],
    queryFn: () => api.get('/api/data-quality/contact-linkage'),
    enabled: started,
    staleTime: 0,
  });
  return (
    <Panel
      title="Kontrola spojení dat kontaktů po importu"
      subtitle="Jen čtení: report nic nemění, nic neodesílá a nevolá SmartEmailing. Ukáže počty a u každého problému deset ukázek (e-maily jsou zkrácené)."
      action={
        <button
          type="button"
          className="btn"
          data-testid="linkage-run"
          disabled={query.isFetching}
          onClick={() => (started ? void query.refetch() : setStarted(true))}
        >
          {started ? 'Spustit znovu' : 'Spustit kontrolu'}
        </button>
      }
    >
      {!started ? (
        <EmptyState title="Kontrola se ještě nespustila">
          Spusťte ji po importu ze SmartEmailingu nebo e-shopu a zjistěte, jestli se data kontaktů
          spojila.
        </EmptyState>
      ) : null}
      {query.isFetching && !query.data ? <Loading what="kontrolu" /> : null}
      {query.isError ? (
        <ErrorState
          title="Kontrolu se nepodařilo provést"
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : null}
      {query.data ? (
        <div className="stack" data-testid="linkage-report">
          <div className="tiles">
            {TOTAL_LABEL.map(([key, text]) => (
              <Tile key={key} label={text} value={num(query.data.totals[key] ?? 0)} />
            ))}
          </div>
          {query.data.issues.map((c) => {
            const meta = ISSUE[c.key];
            return (
              <section key={c.key} data-testid={`linkage-${c.key}`} className="stack">
                <h3>
                  {meta?.title ?? c.key}{' '}
                  <StatusBadge
                    tone={c.count === 0 ? 'ok' : 'warn'}
                    icon={c.count === 0 ? 'check' : 'alert'}
                  >
                    {c.count === 0 ? 'v pořádku' : `${num(c.count)} záznamů`}
                  </StatusBadge>
                </h3>
                {meta ? <p className="note">{meta.help}</p> : null}
                {c.samples.length > 0 ? (
                  <div className="scroll">
                    <table>
                      <thead>
                        <tr>
                          <th>{meta?.sample ?? 'Záznam'}</th>
                          <th>Poznámka</th>
                          <th>Kontakt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.samples.map((s, i) => (
                          <tr key={`${s.label}-${i}`}>
                            <td>{s.label}</td>
                            <td className="muted">{s.detail ?? '—'}</td>
                            <td>
                              {s.contactId ? (
                                <a href={hrefFor('customers', s.contactId)}>karta kontaktu</a>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </section>
            );
          })}
        </div>
      ) : null}
    </Panel>
  );
}
